import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { Loader2, User, MapPin, FileText, Image as ImageIcon, Mail, Shield, Save, Upload } from "lucide-react";
import { crmGetMyProfile, crmUpdateMyProfile } from "@/lib/crm.functions";
import { uploadMyAvatar } from "@/lib/profile.functions";
import { compressImage } from "@/lib/compress-image";
import { SafeAvatar } from "@/components/SafeAvatar";

export const Route = createFileRoute("/crm/profile")({
  component: CRMProfilePage,
});

const BS = {
  primary: "#0d6efd",
  bodyBg: "#f8f9fa",
  cardBorder: "#dee2e6",
  muted: "#6c757d",
  dark: "#212529",
};

function CRMProfilePage() {
  const getProfile = useServerFn(crmGetMyProfile);
  const updateProfile = useServerFn(crmUpdateMyProfile);
  const uploadAvatar = useServerFn(uploadMyAvatar);
  const qc = useQueryClient();

  const q = useQuery({ queryKey: ["crm", "my-profile"], queryFn: () => getProfile() });

  const [form, setForm] = useState({
    display_name: "",
    home_city: "",
    bio: "",
    avatar_url: "",
  });
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (q.data?.profile) {
      setForm({
        display_name: q.data.profile.display_name ?? "",
        home_city: q.data.profile.home_city ?? "",
        bio: q.data.profile.bio ?? "",
        avatar_url: q.data.profile.avatar_url ?? "",
      });
      // Only use a signed URL or absolute http(s) URL. Never fall back to the
      // bare storage path — it would resolve relative to the current route
      // (e.g. `/crm/<userId>/file.png`) and render as a broken image.
      const signed = (q.data.profile as any).avatar_signed_url as string | null | undefined;
      const raw = q.data.profile.avatar_url ?? null;
      const fallback = raw && /^https?:\/\//i.test(raw) ? raw : null;
      setAvatarPreview(signed ?? fallback);
    }
  }, [q.data]);

  async function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
    if (!allowed.includes(file.type as any)) {
      setMsg("Unsupported image type");
      return;
    }
    setUploading(true);
    setMsg(null);
    try {
      const compressed = file.type === "image/gif" ? file : await compressImage(file);
      const dataUrl: string = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result ?? ""));
        r.onerror = () => reject(new Error("Failed to read image"));
        r.readAsDataURL(compressed);
      });
      const uploaded = await uploadAvatar({
        data: { fileName: compressed.name, mimeType: compressed.type as any, dataUrl },
      });
      setForm((f) => ({ ...f, avatar_url: uploaded.path }));
      setAvatarPreview(uploaded.signedUrl ?? null);
      setMsg("Avatar uploaded — click Save to keep it");
    } catch (err: any) {
      setMsg(err?.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  const m = useMutation({
    mutationFn: () =>
      updateProfile({
        data: {
          display_name: form.display_name.trim() || null,
          home_city: form.home_city.trim() || null,
          bio: form.bio.trim() || null,
          avatar_url: form.avatar_url.trim() || null,
        },
      }),
    onSuccess: () => {
      setMsg("Profile saved");
      qc.invalidateQueries({ queryKey: ["crm", "my-profile"] });
      setTimeout(() => setMsg(null), 2500);
    },
    onError: (e: any) => setMsg(e?.message ?? "Failed to save"),
  });

  if (q.isLoading) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" style={{ color: BS.muted }} />
      </div>
    );
  }

  const initials = (form.display_name || q.data?.email || "?")
    .split(/\s+|@/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s: string) => s[0]?.toUpperCase())
    .join("");

  const role = q.data?.role;
  const roleLabel = role === "admin" ? "Admin" : role === "sales_rep" ? "Sales Rep" : "—";

  return (
    <div className="max-w-3xl mx-auto p-4 sm:p-6" style={{ fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <h1 className="text-xl sm:text-2xl font-bold mb-4" style={{ color: BS.dark }}>
        My Profile
      </h1>

      {/* Identity card */}
      <div
        className="bg-white rounded-xl border p-4 sm:p-5 mb-4 flex items-center gap-4"
        style={{ borderColor: BS.cardBorder }}
      >
        <SafeAvatar
          src={avatarPreview}
          name={form.display_name}
          email={q.data?.email}
          className="size-16 rounded-full object-cover border"
          fallbackClassName="size-16 rounded-full grid place-items-center bg-[#0d6efd] text-white text-lg font-bold"
          roundedClassName="rounded-full"
        >
          {initials || <User className="size-6" />}
        </SafeAvatar>
        <div className="min-w-0">
          <div className="font-semibold text-base sm:text-lg truncate" style={{ color: BS.dark }}>
            {form.display_name || "Unnamed"}
          </div>
          <div className="flex items-center gap-1.5 text-sm truncate" style={{ color: BS.muted }}>
            <Mail className="size-3.5" /> <span className="truncate">{q.data?.email}</span>
          </div>
          <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
            style={{ background: "rgba(13,110,253,0.1)", color: BS.primary }}>
            <Shield className="size-3" /> {roleLabel}
          </div>
        </div>
      </div>

      {/* Edit form */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          m.mutate();
        }}
        className="bg-white rounded-xl border p-4 sm:p-5 space-y-4"
        style={{ borderColor: BS.cardBorder }}
      >
        <Field label="Display Name" icon={User}>
          <input
            value={form.display_name}
            onChange={(e) => setForm({ ...form, display_name: e.target.value })}
            className="w-full rounded border px-3 py-2 text-sm"
            style={{ borderColor: BS.cardBorder }}
            placeholder="e.g. Jane Doe"
            maxLength={120}
          />
        </Field>

        <Field label="City" icon={MapPin}>
          <input
            value={form.home_city}
            onChange={(e) => setForm({ ...form, home_city: e.target.value })}
            className="w-full rounded border px-3 py-2 text-sm"
            style={{ borderColor: BS.cardBorder }}
            placeholder="e.g. Helsinki"
            maxLength={120}
          />
        </Field>

        <Field label="Avatar" icon={ImageIcon}>
          <div className="flex items-center gap-3">
            <div
              className="size-12 shrink-0 rounded-full overflow-hidden border grid place-items-center bg-[#f8f9fa]"
              style={{ borderColor: BS.cardBorder }}
            >
              <SafeAvatar
                src={avatarPreview}
                name={form.display_name}
                email={q.data?.email}
                className="size-full object-cover"
                fallbackClassName="size-full grid place-items-center bg-[#f8f9fa] text-[#6c757d] text-sm font-bold"
                roundedClassName="rounded-full"
              >
                <User className="size-5" style={{ color: BS.muted }} />
              </SafeAvatar>
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={onPickFile}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded border text-sm font-medium disabled:opacity-60"
              style={{ borderColor: BS.cardBorder, color: BS.dark }}
            >
              {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
              {avatarPreview ? "Replace Photo" : "Upload Photo"}
            </button>
            {avatarPreview && (
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, avatar_url: "" }));
                  setAvatarPreview(null);
                }}
                className="text-sm"
                style={{ color: BS.muted }}
              >
                Remove
              </button>
            )}
          </div>
          <p className="mt-1 text-[11px]" style={{ color: BS.muted }}>
            JPG, PNG, WebP or GIF · up to 2 MB
          </p>
        </Field>

        <Field label="Bio / Notes" icon={FileText}>
          <textarea
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            className="w-full rounded border px-3 py-2 text-sm min-h-[100px]"
            style={{ borderColor: BS.cardBorder }}
            placeholder="A short bio about yourself"
            maxLength={800}
          />
        </Field>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="submit"
            disabled={m.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-semibold text-white disabled:opacity-60"
            style={{ background: BS.primary }}
          >
            {m.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
            Save
          </button>
          {msg && <span className="text-sm" style={{ color: BS.muted }}>{msg}</span>}
        </div>
      </form>
    </div>
  );
}

function Field({ label, icon: Icon, children }: { label: string; icon: any; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: BS.muted }}>
        <Icon className="size-3.5" /> {label}
      </span>
      {children}
    </label>
  );
}
